/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  ConfigApi,
  createApiRef,
  DiscoveryApi,
} from '@backstage/core-plugin-api';
import { ErrorResponseBody } from '@backstage/errors';

import {
  Account,
  AccountResponse,
  AccountsResponse,
  AccountComplianceReportResponse,
  AccountComplianceSummaryResponse,
  AddAccountRequest,
  AddAccountResponse,
  ConfirmItRequestForAccountRequest,
  CreateAccountResponse,
  FailAccountRequest,
  LogsResponse,
  PendingAccountResponse,
  PendingAccountsResponse,
} from 'plugin-aws-dashboard-common';
import {
  AwsDashboardApi,
  SlackAlertResponse,
  SlackAlertRequest,
} from './types';

const ISO_8601_RE =
  /^([\+-]?\d{4}(?!\d{2}\b))((-?)((0[1-9]|1[0-2])(\3([12]\d|0[1-9]|3[01]))?|W([0-4]\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\d|[12]\d{2}|3([0-5]\d|6[1-6])))([T\s]((([01]\d|2[0-3])((:?)[0-5]\d)?|24\:?00)([\.,]\d+(?!:))?)?(\17[0-5]\d([\.,]\d+)?)?([zZ]|([\+-])([01]\d|2[0-3]):?([0-5]\d)?)?)?)?$/;

export const awsDashboardApiRef = createApiRef<AwsDashboardApi>({
  id: 'plugin.aws.api',
});

type ClientOptions = {
  configApi: ConfigApi;
  discoveryApi: DiscoveryApi;
};

export class AwsDashboardClient implements AwsDashboardApi {
  static fromConfig(options: ClientOptions) {
    return new AwsDashboardClient(options.configApi, options.discoveryApi);
  }

  constructor(
    private readonly configApi: ConfigApi,
    private readonly discoveryApi: DiscoveryApi,
  ) {}

  private getBackendUrl(path: string): Promise<string> {
    return new Promise(async (resolve, _reject) => {
      const backendBaseUrl = await this.discoveryApi.getBaseUrl('aws');
      if (path.startsWith('/')) {
        path = path.slice(1);
      }
      resolve(`${backendBaseUrl}/${path}`);
    });
  }

  private getSlackWorkflowUrl(path: string): Promise<string> {
    return new Promise(async (resolve, _reject) => {
      const proxyBaseUrl = await this.discoveryApi.getBaseUrl('proxy');
      if (path.startsWith('/')) {
        path = path.slice(1);
      }
      resolve(`${proxyBaseUrl}/slack/workflows/${path}`);
    });
  }

  private backendRequest<T>(
    method: 'GET' | 'POST',
    path: string,
    request?: any,
  ): Promise<T> {
    return new Promise(async (resolve, reject) => {
      const backendUrl = await this.getBackendUrl(path);

      let response: Response;
      if (method === 'POST') {
        response = await fetch(backendUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: request && JSON.stringify(request),
        });
      } else {
        response = await fetch(backendUrl);
      }
      const text: string | undefined = await response.text();

      if (response.status >= 500) {
        // Handle backend exception, packaged by Backstage middleware
        const error = JSON.parse(text) as ErrorResponseBody;
        reject(new Error(`Backend Error: ${error.error.message}`));
      } else if (response.status >= 400) {
        // Handle manual error, stored in body text
        reject(new Error(text));
      } else {
        const object: T = text && JSON.parse(text, (_key, value) => {
          // Revive ISO-8601 Dates
          if (typeof value === 'string' && ISO_8601_RE.test(value)) {
            return new Date(value);
          }
          return value;
        });
        resolve(object);
      }
    });
  }

  private backendGet<T>(path: string): Promise<T> {
    return this.backendRequest('GET', path);
  }

  private backendPost<T>(path: string, request?: any): Promise<T> {
    return this.backendRequest('POST', path, request);
  }

  /* Returns a downloadable backend URL */
  private async getComplianceReportDownloadUrl(accountId: string) {
    const backendUrl = await this.getBackendUrl(
      `/accounts/compliance-report/${accountId}/download`,
    );

    return backendUrl;
  }

  /* Fetches an AWS Account by ID */
  getAccount(accountId: string): Promise<AccountResponse> {
    return this.backendGet<Account>(`/accounts/${accountId}/`);
  }

  /**
   * Fetches a list of AWS Accounts
   * On failure, falls back to a local version
   *
   */
  getAccounts(): Promise<AccountsResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        let accounts = await this.backendGet<AccountsResponse>('/accounts/');

        try {
          const summary =
            await this.backendGet<AccountComplianceSummaryResponse>(
              '/accounts/compliance-summary/',
            );

          accounts = accounts.map(account => ({
            ...account,
            noncompliantResources: summary.find(
              entry => account.id === entry.accountId,
            )?.count,
          }));
        } catch {}

        resolve(accounts);
      } catch (err) {
        reject(err);
      }
    });
  }

  /* Fetches a ComplianceReport */
  getComplianceReport(
    accountId: string,
  ): Promise<AccountComplianceReportResponse> {
    return this.backendGet<AccountComplianceReportResponse>(
      `/accounts/compliance-report/${accountId}`,
    );
  }

  /* Generates a URL to download a ComplianceReport */
  getComplianceReportCsvUrl(accountId: string): Promise<string> {
    return this.getComplianceReportDownloadUrl(accountId);
  }

  /* Creates a request for a new AWS Account */
  addAccount(request: AddAccountRequest): Promise<AddAccountResponse> {
    return this.backendPost<AddAccountResponse>(
      '/pending-accounts/add',
      request,
    );
  }

  /* Get a PendingAccount by id */
  getPendingAccount(accountId: number): Promise<PendingAccountResponse> {
    return this.backendGet<PendingAccountResponse>(
      `/pending-accounts/${accountId}/`,
    );
  }

  /* Get PendingAccounts */
  getPendingAccounts(): Promise<PendingAccountsResponse> {
    return this.backendGet<PendingAccountsResponse>(
      '/pending-accounts/active/',
    );
  }

  /* Mark IT Request sent for the given environment */
  confirmItRequestForAccount(
    accountId: number,
    request: ConfirmItRequestForAccountRequest,
  ): Promise<void> {
    return this.backendPost<void>(
      `/pending-accounts/${accountId}/confirm-it-request`,
      request,
    );
  }

  /* Approve a PendingAccount with the current user */
  approveAccount(accountId: number): Promise<void> {
    return this.backendPost<void>(`/pending-accounts/${accountId}/approve`);
  }

  /* Delete a stale PendingAccount request */
  deleteAccount(accountId: number): Promise<void> {
    return this.backendPost<void>(`/pending-accounts/${accountId}/delete`);
  }

  /* Get the Lambda logs for a PendingAccount */
  getPendingAccountLogs(accountId: number): Promise<LogsResponse> {
    return this.backendGet<LogsResponse>(
      `/pending-accounts/${accountId}/logs/`,
    );
  }

  /* Mark PendingAccount creation as failed, attaching error message */
  failAccount(accountId: number, request: FailAccountRequest): Promise<void> {
    return this.backendPost<void>(
      `/pending-accounts/${accountId}/fail`,
      request,
    );
  }

  /* Check approvals and create PendingAccount */
  createAccount(accountId: number): Promise<CreateAccountResponse> {
    return this.backendPost<CreateAccountResponse>(
      `/pending-accounts/${accountId}/create`,
    );
  }

  /**
   * Send a Slack alert to #backstage
   * TODO: Refactor into standalone plugin if it needs to be reused
   *
   */
  sendSlackAlert(request: SlackAlertRequest): Promise<SlackAlertResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        const slackWorkflow = this.configApi.getString(
          'awsDashboard.createAccount.slackWorkflow',
        );
        const response = await fetch(
          await this.getSlackWorkflowUrl(slackWorkflow),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request),
          },
        );

        resolve({
          status: response.status,
          error: await response.text(),
        });
      } catch (err) {
        reject(err);
      }
    });
  }
}
